/**
 * Page Performance Monitor
 * 
 * Specialized monitoring for page-wise performance tracking with detailed metrics
 */

class PagePerformanceMonitor {
  constructor() {
    this.pageMetrics = new Map();
    this.currentPage = null;
    this.pageStartTime = null;
    this.userInteractions = [];
    this.apiCalls = [];
    this.errors = [];
    this.sessionId = this.generateSessionId();
    this.startTime = Date.now();
    this.lastUrl = window.location.href;
    
    // Initialize monitoring
    this.initializePageMonitoring();
    
    // Monitor URL changes for SPA navigation
    this.monitorUrlChanges();
  }

  generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Monitor URL changes for SPA navigation
   */
  monitorUrlChanges() {
    // Monitor for URL changes (SPA navigation)
    let currentUrl = window.location.href;
    
    // Check for URL changes every 100ms
    setInterval(() => {
      if (window.location.href !== currentUrl) {
        currentUrl = window.location.href;
        console.log(`🔄 URL changed to: ${currentUrl}`);
        this.handlePageNavigation();
      }
    }, 100);
    
    // Also listen for popstate events (browser back/forward)
    window.addEventListener('popstate', () => {
      console.log(`🔄 Popstate event: ${window.location.href}`);
      this.handlePageNavigation();
    });
    
    // Listen for pushstate/replacestate events (programmatic navigation)
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;
    
    window.history.pushState = function(...args) {
      originalPushState.apply(window.history, args);
      console.log(`🔄 PushState: ${window.location.href}`);
      this.handlePageNavigation();
    }.bind(this);
    
    window.history.replaceState = function(...args) {
      originalReplaceState.apply(window.history, args);
      console.log(`🔄 ReplaceState: ${window.location.href}`);
      this.handlePageNavigation();
    }.bind(this);
  }

  /**
   * Handle page navigation
   */
  handlePageNavigation() {
    const newPageName = this.getCurrentPageName();
    
    // If it's a new page, start monitoring it
    if (newPageName !== this.currentPage) {
      console.log(`📄 Navigating to: ${newPageName}`);
      this.currentPage = newPageName;
      this.pageStartTime = performance.now();
      
      // Start monitoring the new page
      this.monitorPageLoad();
      
      // Trigger dashboard update
      this.triggerDashboardUpdate();
    }
  }

  /**
   * Trigger dashboard update
   */
  triggerDashboardUpdate() {
    // Dispatch custom event to notify dashboard
    const updateEvent = new CustomEvent('pageMetricsUpdated', {
      detail: {
        pageName: this.currentPage,
        timestamp: new Date().toISOString()
      }
    });
    window.dispatchEvent(updateEvent);
    
    console.log(`📊 Dashboard update triggered for: ${this.currentPage}`);
  }

  /**
   * Initialize page monitoring
   */
  initializePageMonitoring() {
    // Monitor page loads
    this.monitorPageLoad();
    
    // Monitor user interactions
    this.monitorUserInteractions();
    
    // Monitor API calls
    this.monitorApiCalls();
    
    // Monitor errors
    this.monitorErrors();
    
    // Monitor performance metrics
    this.monitorPerformanceMetrics();
    
    console.log('🚀 Page Performance Monitor initialized');
  }

  /**
   * Monitor page load performance
   */
  monitorPageLoad() {
    const pageName = this.getCurrentPageName();
    const startTime = typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
    
    this.currentPage = pageName;
    this.pageStartTime = startTime;
    
    // Check if page already exists in metrics
    if (!this.pageMetrics.has(pageName)) {
      // Track page load metrics
      const pageLoadData = {
        pageName,
        startTime,
        timestamp: new Date().toISOString(),
        sessionId: this.sessionId,
        url: window.location.href,
        userAgent: navigator.userAgent,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight
        },
        performance: {
          navigationStart: typeof performance !== 'undefined' && performance.timing ? performance.timing.navigationStart : 0,
          domContentLoaded: typeof performance !== 'undefined' && performance.timing ? performance.timing.domContentLoadedEventEnd - performance.timing.navigationStart : 0,
          loadComplete: typeof performance !== 'undefined' && performance.timing ? performance.timing.loadEventEnd - performance.timing.navigationStart : 0
        }
      };
      
      this.pageMetrics.set(pageName, {
        ...pageLoadData,
        interactions: [],
        apiCalls: [],
        errors: [],
        metrics: {},
        frontendDuration: 0,
        apiDuration: 0,
        totalDuration: 0,
        lastVisited: new Date().toISOString()
      });
      
      console.log(`📄 Page Load: ${pageName} started at ${new Date().toLocaleTimeString()}`);
    } else {
      // Update last visited time for existing page
      const pageData = this.pageMetrics.get(pageName);
      pageData.lastVisited = new Date().toISOString();
      this.persistPageMetrics(pageName, pageData);
    }
    
    // Track when page is fully loaded
    const handleLoad = () => {
      const loadTime = (typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now()) - startTime;
      const pageData = this.pageMetrics.get(pageName);
      
      if (pageData) {
        pageData.loadComplete = true;
        pageData.loadTime = loadTime;
        pageData.frontendDuration = loadTime; // Initial frontend duration
        pageData.metrics.pageLoad = {
          duration: loadTime,
          timestamp: new Date().toISOString(),
          performance: this.getPerformanceLevel(loadTime)
        };
        
        console.log(`✅ Page Load Complete: ${pageName} took ${loadTime.toFixed(2)}ms`);
        this.persistPageMetrics(pageName, pageData);
        this.triggerDashboardUpdate();
      }
    };
    
    // If page is already loaded, handle immediately
    if (document.readyState === 'complete') {
      handleLoad();
    } else {
      window.addEventListener('load', handleLoad);
    }
  }

  /**
   * Monitor user interactions
   */
  monitorUserInteractions() {
    const interactionTypes = ['click', 'input', 'scroll', 'resize', 'keydown'];
    
    interactionTypes.forEach(type => {
      document.addEventListener(type, (event) => {
        const pageName = this.getCurrentPageName();
        const pageData = this.pageMetrics.get(pageName);
        
        if (pageData) {
          const interaction = {
            type,
            target: event.target.tagName || 'unknown',
            targetId: event.target.id || '',
            targetClass: event.target.className || '',
            timestamp: new Date().toISOString(),
            position: {
              x: event.clientX || 0,
              y: event.clientY || 0
            },
            details: this.getInteractionDetails(event)
          };
          
          pageData.interactions.push(interaction);
          
          // Track interaction performance
          const interactionTime = (typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now()) - this.pageStartTime;
          pageData.metrics[`${type}Interaction`] = {
            count: (pageData.metrics[`${type}Interaction`]?.count || 0) + 1,
            lastTime: interactionTime,
            timestamp: new Date().toISOString()
          };
          
          // Update frontend duration
          pageData.frontendDuration += interactionTime;
          pageData.totalDuration = pageData.frontendDuration + pageData.apiDuration;
          
          this.persistPageMetrics(pageName, pageData);
          
          // Trigger dashboard update for significant interactions
          if (type === 'click' || type === 'input') {
            this.triggerDashboardUpdate();
          }
        }
      }, { passive: true });
    });
  }

  /**
   * Monitor API calls
   */
  monitorApiCalls() {
    // Override fetch to monitor API calls
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const pageName = this.getCurrentPageName();
      const pageData = this.pageMetrics.get(pageName);
      const startTime = typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
      const url = args[0];
      
      try {
        const response = await originalFetch(...args);
        const endTime = typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
        const duration = endTime - startTime;
        
        if (pageData) {
          const apiCall = {
            url: typeof url === 'string' ? url : url.url,
            method: args[1]?.method || 'GET',
            startTime,
            endTime,
            duration,
            status: response.status,
            success: response.ok,
            timestamp: new Date().toISOString()
          };
          
          pageData.apiCalls.push(apiCall);
          
          // Update API duration
          pageData.apiDuration += duration;
          pageData.totalDuration = pageData.frontendDuration + pageData.apiDuration;
          
          // Track API performance
          const apiKey = `api_${apiCall.method}_${this.getApiEndpoint(url)}`;
          pageData.metrics[apiKey] = {
            count: (pageData.metrics[apiKey]?.count || 0) + 1,
            totalTime: (pageData.metrics[apiKey]?.totalTime || 0) + duration,
            averageTime: ((pageData.metrics[apiKey]?.totalTime || 0) + duration) / ((pageData.metrics[apiKey]?.count || 0) + 1),
            lastTime: duration,
            success: response.ok,
            timestamp: new Date().toISOString()
          };
          
          this.persistPageMetrics(pageName, pageData);
          
          // Trigger dashboard update for API calls
          this.triggerDashboardUpdate();
        }
        
        return response;
      } catch (error) {
        const endTime = typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
        const duration = endTime - startTime;
        
        if (pageData) {
          const apiCall = {
            url: typeof url === 'string' ? url : url.url,
            method: args[1]?.method || 'GET',
            startTime,
            endTime,
            duration,
            status: 'error',
            success: false,
            error: error.message,
            timestamp: new Date().toISOString()
          };
          
          pageData.apiCalls.push(apiCall);
          pageData.errors.push({
            type: 'api_error',
            message: error.message,
            timestamp: new Date().toISOString()
          });
          
          // Update API duration even for failed calls
          pageData.apiDuration += duration;
          pageData.totalDuration = pageData.frontendDuration + pageData.apiDuration;
          
          this.persistPageMetrics(pageName, pageData);
          
          // Trigger dashboard update for errors
          this.triggerDashboardUpdate();
        }
        
        throw error;
      }
    };
  }

  /**
   * Monitor errors
   */
  monitorErrors() {
    // Monitor JavaScript errors
    window.addEventListener('error', (event) => {
      const pageName = this.getCurrentPageName();
      const pageData = this.pageMetrics.get(pageName);
      
      if (pageData) {
        const error = {
          type: 'javascript_error',
          message: event.message,
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          timestamp: new Date().toISOString()
        };
        
        pageData.errors.push(error);
        this.persistPageMetrics(pageName, pageData);
        this.triggerDashboardUpdate();
      }
    });
    
    // Monitor unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      const pageName = this.getCurrentPageName();
      const pageData = this.pageMetrics.get(pageName);
      
      if (pageData) {
        const error = {
          type: 'promise_rejection',
          message: event.reason?.message || 'Unhandled Promise Rejection',
          timestamp: new Date().toISOString()
        };
        
        pageData.errors.push(error);
        this.persistPageMetrics(pageName, pageData);
        this.triggerDashboardUpdate();
      }
    });
  }

  /**
   * Monitor performance metrics
   */
  monitorPerformanceMetrics() {
    // Monitor Core Web Vitals
    if ('PerformanceObserver' in window) {
      // Monitor Largest Contentful Paint (LCP)
      const lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1];
        
        const pageName = this.getCurrentPageName();
        const pageData = this.pageMetrics.get(pageName);
        
        if (pageData) {
          pageData.metrics.lcp = {
            value: lastEntry.startTime,
            timestamp: new Date().toISOString(),
            performance: this.getPerformanceLevel(lastEntry.startTime)
          };
          
          this.persistPageMetrics(pageName, pageData);
          this.triggerDashboardUpdate();
        }
      });
      
      try {
        lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
      } catch (e) {
        console.warn('LCP monitoring not supported');
      }
      
      // Monitor First Input Delay (FID)
      const fidObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        
        entries.forEach(entry => {
          const pageName = this.getCurrentPageName();
          const pageData = this.pageMetrics.get(pageName);
          
          if (pageData) {
            pageData.metrics.fid = {
              value: entry.processingStart - entry.startTime,
              timestamp: new Date().toISOString(),
              performance: this.getPerformanceLevel(entry.processingStart - entry.startTime)
            };
            
            this.persistPageMetrics(pageName, pageData);
            this.triggerDashboardUpdate();
          }
        });
      });
      
      try {
        fidObserver.observe({ entryTypes: ['first-input'] });
      } catch (e) {
        console.warn('FID monitoring not supported');
      }
    }
  }

  /**
   * Get current page name
   */
  getCurrentPageName() {
    const path = window.location.pathname;
    const pageMap = {
      '/listofentities': 'List of Entities',
      '/performance-dashboard': 'Performance Dashboard',
      '/': 'Home',
      '/editconfig': 'Edit Config',
      '/deepdive': 'Deep Dive',
      '/listofbusinessunits': 'List of Business Units',
      '/listsitelocations': 'List Site Locations'
    };
    
    return pageMap[path] || path.replace('/', '').replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }

  /**
   * Get API endpoint name
   */
  getApiEndpoint(url) {
    if (typeof url === 'string') {
      const urlObj = new URL(url, window.location.origin);
      return urlObj.pathname.split('/').pop() || 'unknown';
    }
    return 'unknown';
  }

  /**
   * Get interaction details
   */
  getInteractionDetails(event) {
    switch (event.type) {
      case 'click':
        return {
          button: event.button,
          ctrlKey: event.ctrlKey,
          shiftKey: event.shiftKey,
          altKey: event.altKey
        };
      case 'input':
        return {
          inputType: event.inputType,
          data: event.data
        };
      case 'scroll':
        return {
          scrollX: window.scrollX,
          scrollY: window.scrollY
        };
      default:
        return {};
    }
  }

  /**
   * Get performance level
   */
  getPerformanceLevel(duration) {
    if (duration <= 200) return 'Excellent';
    if (duration <= 500) return 'Good';
    if (duration <= 1000) return 'Fair';
    return 'Poor';
  }

  /**
   * Persist page metrics to localStorage
   */
  persistPageMetrics(pageName, pageData) {
    try {
      const storageKey = `pageMetrics_${pageName}`;
      localStorage.setItem(storageKey, JSON.stringify(pageData));
    } catch (error) {
      console.warn('Failed to persist page metrics:', error);
    }
  }

  /**
   * Load page metrics from localStorage
   */
  loadPageMetrics(pageName) {
    try {
      const storageKey = `pageMetrics_${pageName}`;
      const data = localStorage.getItem(storageKey);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.warn('Failed to load page metrics:', error);
      return null;
    }
  }

  /**
   * Generate comprehensive page report
   */
  generatePageReport() {
    const report = {
      session: {
        sessionId: this.sessionId,
        startTime: this.startTime,
        duration: Date.now() - this.startTime,
        totalPages: this.pageMetrics.size,
        currentPage: this.getCurrentPageName()
      },
      pages: {},
      summary: {},
      apiMetrics: {},
      frontendMetrics: {},
      detailedMetrics: {}
    };

    // Process each page
    this.pageMetrics.forEach((pageData, pageName) => {
      // Calculate performance level
      const loadTime = pageData.loadTime || 0;
      const performance = this.getPerformanceLevel(loadTime);
      
      const pageReport = {
        pageName,
        loadTime: pageData.loadTime || 0,
        frontendDuration: pageData.frontendDuration || 0,
        apiDuration: pageData.apiDuration || 0,
        totalDuration: pageData.totalDuration || 0,
        interactions: pageData.interactions.length,
        apiCalls: pageData.apiCalls.length,
        errors: pageData.errors.length,
        performance,
        lastVisited: pageData.lastVisited || new Date().toISOString()
      };
      
      report.pages[pageName] = pageReport;
      
      // Add to detailed metrics for other tabs
      report.detailedMetrics[`${pageName}-pageLoad`] = {
        count: 1,
        average: pageData.loadTime || 0,
        min: pageData.loadTime || 0,
        max: pageData.loadTime || 0,
        median: pageData.loadTime || 0,
        successRate: 100,
        performance: performance,
        lastExecuted: pageData.lastVisited || new Date().toISOString()
      };
      
      // Add API metrics
      if (pageData.apiCalls.length > 0) {
        const apiDurations = pageData.apiCalls.map(call => call.duration);
        const avgApiDuration = apiDurations.reduce((a, b) => a + b, 0) / apiDurations.length;
        const successRate = (pageData.apiCalls.filter(call => call.success).length / pageData.apiCalls.length) * 100;
        
        report.apiMetrics[`${pageName}-api`] = {
          count: pageData.apiCalls.length,
          average: avgApiDuration,
          min: Math.min(...apiDurations),
          max: Math.max(...apiDurations),
          successRate: successRate,
          performance: this.getPerformanceLevel(avgApiDuration),
          lastExecuted: pageData.lastVisited || new Date().toISOString()
        };
      }
      
      // Add frontend metrics
      if (pageData.interactions.length > 0) {
        const interactionTimes = pageData.interactions.map(() => {
          const currentTime = typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
          return currentTime - this.pageStartTime;
        });
        const avgInteractionTime = interactionTimes.reduce((a, b) => a + b, 0) / interactionTimes.length;
        
        report.frontendMetrics[`${pageName}-interactions`] = {
          count: pageData.interactions.length,
          average: avgInteractionTime,
          min: Math.min(...interactionTimes),
          max: Math.max(...interactionTimes),
          successRate: 100,
          performance: this.getPerformanceLevel(avgInteractionTime),
          lastExecuted: pageData.lastVisited || new Date().toISOString()
        };
      }
    });

    // Generate summary
    const allLoadTimes = Object.values(report.pages).map(page => page.loadTime);
    const allFrontendDurations = Object.values(report.pages).map(page => page.frontendDuration);
    const allApiDurations = Object.values(report.pages).map(page => page.apiDuration);
    if (allLoadTimes.length > 0) {
      report.summary = {
        totalPages: this.pageMetrics.size,
        averageLoadTime: allLoadTimes.reduce((a, b) => a + b, 0) / allLoadTimes.length,
        averageFrontendDuration: allFrontendDurations.reduce((a, b) => a + b, 0) / allFrontendDurations.length,
        averageApiDuration: allApiDurations.reduce((a, b) => a + b, 0) / allApiDurations.length,
        fastestPage: Math.min(...allLoadTimes),
        slowestPage: Math.max(...allLoadTimes),
        totalInteractions: Object.values(report.pages).reduce((sum, page) => sum + page.interactions, 0),
        totalApiCalls: Object.values(report.pages).reduce((sum, page) => sum + page.apiCalls, 0),
        totalErrors: Object.values(report.pages).reduce((sum, page) => sum + page.errors, 0)
      };
    }

    return report;
  }

  /**
   * Log page report to console
   */
  logPageReport() {
    const report = this.generatePageReport();
    console.group('📊 Page Performance Report');
    console.table(report.pages);
    console.groupEnd();
    return report;
  }

  /**
   * Clear all page metrics
   */
  clearPageMetrics() {
    this.pageMetrics.clear();
    
    // Clear from localStorage
    try {
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('pageMetrics_')) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.warn('Failed to clear page metrics from localStorage:', error);
    }
    
    console.log('🗑️ All page metrics cleared');
  }

  /**
   * Export page metrics
   */
  exportPageMetrics() {
    const report = this.generatePageReport();
    const dataStr = JSON.stringify(report, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `page-performance-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
  }
}

// Create and export singleton instance
const pagePerformanceMonitor = new PagePerformanceMonitor();
export default pagePerformanceMonitor;
